import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { DeveloperDashboardService, DeveloperProjectSummary } from '../services/developer-dashboard.service';
import { DeveloperReportsService } from '../services/developer-reports.service';
import { HttpError } from '../api/http';
import styles from './DeveloperDashboardPage.module.css';

export default function DeveloperDashboardPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { addNotification } = useNotification();

  const [projects, setProjects] = useState<DeveloperProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingReport, setDownloadingReport] = useState<'pdf' | 'csv' | null>(null);

  const locale = i18n.language.startsWith('es') ? 'es-MX' : 'en-US';
  const currency = i18n.language.startsWith('es') ? 'MXN' : 'USD';

  const formatMoney = (value: number) =>
    Number(value || 0).toLocaleString(locale, { style: 'currency', currency, minimumFractionDigits: 0 });

  const normalizeStatus = (status?: string | null) => {
    const key = (status || '').toUpperCase();
    if (['BORRADOR', 'DRAFT', 'PLANNING'].includes(key)) return 'DRAFT';
    if (['PUBLICADO', 'PUBLISHED', 'PRE_SALE'].includes(key)) return 'PUBLISHED';
    if (['EN EJECUCIÓN', 'EN EJECUCION', 'IN_EXECUTION', 'EXECUTION', 'UNDER_CONSTRUCTION', 'DELIVERING'].includes(key)) {
      return 'IN_EXECUTION';
    }
    if (['FINALIZADO', 'FINISHED', 'COMPLETED'].includes(key)) return 'FINISHED';
    if (['CANCELADO', 'CANCELLED', 'CANCELED', 'ON_HOLD'].includes(key)) return 'CANCELLED';
    return key || '';
  };

  const formatStatus = (status?: string | null) => {
    const key = normalizeStatus(status);
    const map: Record<string, string> = {
      DRAFT: t('developer_panel.status_draft') || 'Borrador',
      PUBLISHED: t('developer_panel.status_published') || 'Publicado',
      IN_EXECUTION: t('developer_panel.status_in_execution') || 'En ejecución',
      FINISHED: t('developer_panel.status_finished') || 'Finalizado',
      CANCELLED: t('developer_panel.status_cancelled') || 'Cancelado'
    };
    return map[key] || status || t('developer_panel.status_unknown') || '—';
  };

  const statusTone = (status?: string | null) => {
    const key = normalizeStatus(status);
    if (key === 'FINISHED') return styles.statusDone;
    if (key === 'IN_EXECUTION') return styles.statusActive;
    if (key === 'PUBLISHED') return styles.statusPublished;
    if (key === 'CANCELLED') return styles.statusCancelled;
    if (key === 'DRAFT') return styles.statusDraft;
    return styles.statusNeutral;
  };

  const documentStatus = (project: DeveloperProjectSummary) => {
    if (project.requiredDocumentsCount <= 0) {
      return {
        label: 'Sin catálogo',
        detail: 'Tipos no configurados',
        className: styles.docsReview,
      };
    }
    if (project.documentsApproved) {
      return {
        label: 'Documentos aprobados',
        detail: `${project.approvedDocumentsCount}/${project.requiredDocumentsCount}`,
        className: styles.docsApproved,
      };
    }
    if (project.missingDocumentsCount > 0) {
      return {
        label: 'Faltan documentos',
        detail: `${project.uploadedDocumentsCount}/${project.requiredDocumentsCount}`,
        className: styles.docsMissing,
      };
    }
    if (project.observedDocumentsCount > 0) {
      return {
        label: 'Documentos observados',
        detail: `${project.observedDocumentsCount} por corregir`,
        className: styles.docsObserved,
      };
    }
    return {
      label: 'En revisión',
      detail: `${project.pendingDocumentsCount || project.uploadedDocumentsCount}/${project.requiredDocumentsCount}`,
      className: styles.docsReview,
    };
  };

  const summary = useMemo(() => {
    const totalProjects = projects.length;
    const totalFunded = projects.reduce((acc, p) => acc + Number(p.fundedAmount || 0), 0);
    const totalTarget = projects.reduce((acc, p) => acc + Number(p.targetAmount || 0), 0);
    const totalInvestors = projects.reduce((acc, p) => acc + Number(p.investorsCount || 0), 0);
    const activeProjects = projects.filter((p) => {
      const status = normalizeStatus(p.status);
      return ['IN_EXECUTION', 'PUBLISHED'].includes(status);
    }).length;
    const finishedProjects = projects.filter((p) => {
      const status = normalizeStatus(p.status);
      return ['FINISHED'].includes(status);
    }).length;
    const projectsWithMissingDocs = projects.filter((p) => !p.documentsApproved).length;
    const globalPct = totalTarget > 0 ? Math.round((totalFunded / totalTarget) * 100) : 0;
    return {
      totalProjects,
      totalFunded,
      totalTarget,
      totalInvestors,
      activeProjects,
      finishedProjects,
      projectsWithMissingDocs,
      globalPct
    };
  }, [projects]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await DeveloperDashboardService.getMyProjects();
      setProjects(Array.isArray(data) ? data : []);
    } catch (error: any) {
      const status = error instanceof HttpError ? error.status : error?.status;
      if (status === 403 || error?.payload?.statusCode === 403) {
        const msg =
          t('developer_panel.no_access_subtitle') +
          ' ' +
          (t('developer_panel.relogin_hint') || 'Cierra sesión e inicia de nuevo para actualizar tu rol.');
        setError(msg);
        addNotification(msg, 'warning');
      } else {
        setError(t('developer_panel.load_error') || 'No se pudo cargar la información.');
        addNotification(t('developer_panel.load_error') || 'No se pudo cargar la información.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'developer') {
      load();
    } else {
      setLoading(false);
    }
  }, [user?.role]);

  const downloadGeneralReport = async (format: 'pdf' | 'csv') => {
    setDownloadingReport(format);
    try {
      await DeveloperReportsService.downloadReport({ format });
    } catch (error: any) {
      addNotification(error?.message || 'No se pudo descargar el reporte.', 'error');
    } finally {
      setDownloadingReport(null);
    }
  };

  if (!user || user.role !== 'developer') {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>🔒</div>
          <h2>{t('developer_panel.no_access_title')}</h2>
          <p>{t('developer_panel.no_access_subtitle')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>{t('developer_panel.title')}</h1>
          <p className={styles.subtitle}>{t('developer_panel.subtitle')}</p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.secondaryButton} onClick={() => downloadGeneralReport('pdf')} disabled={!!downloadingReport}>
            {downloadingReport === 'pdf' ? 'Generando...' : 'Reporte PDF'}
          </button>
          <button className={styles.secondaryButton} onClick={() => downloadGeneralReport('csv')} disabled={!!downloadingReport}>
            {downloadingReport === 'csv' ? 'Generando...' : 'Reporte CSV'}
          </button>
          <button className={styles.refreshButton} onClick={load}>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {error ? (
        <div className={styles.errorBanner} role="alert">
          {error}
        </div>
      ) : null}

      {projects.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>📭</div>
          <h2>{t('developer_panel.empty_title')}</h2>
          <p>{t('developer_panel.empty_subtitle')}</p>
        </div>
      ) : (
        <>
          <div className={styles.documentsNotice}>
            <div>
              <strong>Documentos requeridos del proyecto</strong>
              <p>
                Al crear un proyecto, sube sus documentos requeridos. Un administrador debe revisarlos y aprobar el desarrollo antes de que se publique para inversionistas.
              </p>
            </div>
            <span className={summary.projectsWithMissingDocs > 0 ? styles.noticeBadgeWarning : styles.noticeBadgeOk}>
              {summary.projectsWithMissingDocs > 0
                ? `${summary.projectsWithMissingDocs} pendientes`
                : 'Todo listo'}
            </span>
          </div>

          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <div className={styles.summaryLabel}>{t('developer_panel.summary_total_projects') || 'Proyectos'}</div>
              <div className={styles.summaryValue}>{summary.totalProjects}</div>
              <div className={styles.summarySub}>{t('developer_panel.summary_total_projects_hint') || 'Total registrados'}</div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryLabel}>{t('developer_panel.summary_active_projects') || 'En ejecución'}</div>
              <div className={styles.summaryValue}>{summary.activeProjects}</div>
              <div className={styles.summarySub}>{t('developer_panel.summary_active_projects_hint') || 'Publicados / en curso'}</div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryLabel}>{t('developer_panel.summary_finished_projects') || 'Finalizados'}</div>
              <div className={styles.summaryValue}>{summary.finishedProjects}</div>
              <div className={styles.summarySub}>{t('developer_panel.summary_finished_projects_hint') || 'Cerrados'}</div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryLabel}>{t('developer_panel.summary_total_funded') || 'Monto fondeado'}</div>
              <div className={styles.summaryValue}>{formatMoney(summary.totalFunded)}</div>
              <div className={styles.summarySub}>{t('developer_panel.summary_total_funded_hint') || 'Confirmado'}</div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryLabel}>{t('developer_panel.summary_total_target') || 'Objetivo total'}</div>
              <div className={styles.summaryValue}>{formatMoney(summary.totalTarget)}</div>
              <div className={styles.summarySub}>{t('developer_panel.summary_total_target_hint') || 'Meta acumulada'}</div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryLabel}>{t('developer_panel.summary_total_investors') || 'Inversionistas'}</div>
              <div className={styles.summaryValue}>{summary.totalInvestors}</div>
              <div className={styles.summarySub}>{t('developer_panel.summary_total_investors_hint') || 'Conteo confirmado'}</div>
            </div>
            <div className={styles.summaryCardWide}>
              <div className={styles.summaryLabel}>{t('developer_panel.summary_global_progress') || 'Avance global'}</div>
              <div className={styles.summaryValue}>{summary.globalPct}%</div>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${Math.min(summary.globalPct, 100)}%` }} />
              </div>
              <div className={styles.summarySub}>
                {formatMoney(summary.totalFunded)} / {formatMoney(summary.totalTarget)}
              </div>
            </div>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('developer_panel.table_project')}</th>
                  <th>{t('developer_panel.table_status')}</th>
                  <th>{t('developer_panel.table_funded_pct')}</th>
                  <th>{t('developer_panel.table_funded_amount')}</th>
                  <th>{t('developer_panel.table_target_amount')}</th>
                  <th>{t('developer_panel.table_investors')}</th>
                  <th>{t('developer_panel.table_start')}</th>
                  <th>{t('developer_panel.table_end')}</th>
                  <th>Documentos</th>
                  <th>{t('common.actions') || 'Acciones'}</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => {
                  const docs = documentStatus(project);
                  return (
                    <tr key={project.id}>
                      <td className={styles.projectName}>{project.name}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${statusTone(project.status)}`}>
                          {formatStatus(project.status)}
                        </span>
                      </td>
                      <td>
                        <span className={styles.badge}>{project.fundedPct}%</span>
                      </td>
                      <td>{formatMoney(project.fundedAmount)}</td>
                      <td>{formatMoney(project.targetAmount)}</td>
                      <td>{project.investorsCount}</td>
                      <td>
                        {project.fundingStart
                          ? new Date(project.fundingStart).toLocaleDateString(locale)
                          : '—'}
                      </td>
                      <td>
                        {project.fundingEnd
                          ? new Date(project.fundingEnd).toLocaleDateString(locale)
                          : '—'}
                      </td>
                      <td>
                        <span className={`${styles.docsBadge} ${docs.className}`}>
                          {docs.label}
                          <small>{docs.detail}</small>
                        </span>
                      </td>
                      <td>
                        <div className={styles.actions}>
                          <Link to={`/developer/proyectos/${project.id}/edit`} className={`${styles.actionButton} ${styles.actionPrimary}`}>
                            {t('common.edit') || 'Editar'}
                          </Link>
                          <Link to={`/developer/proyectos/${project.id}/documentos`} className={styles.actionButton}>
                            {t('developer_panel.upload_documents') || 'Documentos'}
                          </Link>
                          <Link to={`/developer/proyectos/${project.id}/reportes`} className={styles.actionButton}>
                            Reportes
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
